import { ArrowLeftOutlined } from "@ant-design/icons"
import { Button, Empty, Flex, Form, Skeleton, Typography } from "antd"
import React, { useCallback, useEffect, useState } from "react"
import { type ConnectedProps, connect } from "react-redux"
import { useNavigate } from "react-router"
import { Link, useSearchParams } from "react-router-dom"

import type { DocumentScopeSectionProjectTypes } from "../../../components/Proposal&Contracts/document.types.ts"
import { generateDocumentTitle } from "../../../constants/document-title.ts"
import { RoutesPaths } from "../../../constants/routes.ts"
import type { RootState } from "../../../store.ts"
import { fetchProposal } from "../../Proposals/redux/proposals.thunks.ts"
import {
  createContract,
  generateContract,
  getContractPreview,
  saveContract,
  sendContractToApproval
} from "../redux/contracts.thunks.ts"
import type { ContractRequestData } from "../redux/contracts.thunks.types.ts"
import { ContractAddPreviewHTML } from "./components/ContractAddPreviewHTML.tsx"
import { ContractForm } from "./components/ContractForm.tsx"
import type { ContractFormData } from "./components/contract-form-data.tsx"
import { defaultClientResponsibilities } from "./components/sections/ContractClientResponsibilitiesSection.tsx"
import { defaultContractFormCostSummary } from "./components/sections/ContractCostSummarySection.tsx"
import { defaultExclusions } from "./components/sections/ContractExclusionsSection.tsx"
import { defaultInclusions } from "./components/sections/ContractInclusionsSection.tsx"
import { defaultTermsAndConditions } from "./components/sections/ContractTermsSection.tsx"
import { defaultContractFormWarrantyItems } from "./components/sections/ContractWarrantySection.tsx"

function ContractAddPage({
  saving,
  createContract,
  createContractPreview,
  generateContract,
  getProposal,
  saveContract,
  sendContractToApproval
}: ConnectedProps<typeof connector>) {
  const [form] = Form.useForm<ContractFormData>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [previewHTML, setPreviewHTML] = useState<string | null>(null)
  const [formData, setFormData] = useState<ContractRequestData | null>(null)
  const [fetchingInitialData, setFetchingInitialData] = useState(false)
  const [projectTypes, setProjectTypes] =
    useState<DocumentScopeSectionProjectTypes>([])

  const leadId = searchParams.get("leadID")
  const proposalId = searchParams.get("proposalID")

  const handleInitialForm = useCallback(async () => {
    if (!leadId || !proposalId) return

    try {
      setFetchingInitialData(true)

      const res = await getProposal(proposalId)?.unwrap()
      const data = res?.proposal_data
      const projectTypeDetails = res?.project_type_details

      if (data) {
        const customer = data?.customer || {}
        const company = data?.company || {}
        const visibleBlocks =
          (res?.visible_blocks as ContractFormData["visible_blocks"]) || [
            "customer",
            "project_phases",
            "inclusions",
            "exclusions",
            "client_responsibilities",
            "cost_summary",
            "payment_schedule",
            "warranty",
            "terms_and_conditions"
          ]

        const defaults = {
          inclusions: defaultInclusions,
          exclusions: defaultExclusions,
          client_responsibilities: defaultClientResponsibilities,
          terms_and_conditions: defaultTermsAndConditions,
          cost_summary: defaultContractFormCostSummary
        }

        form.setFieldsValue({
          ...defaults,
          ...data,
          customer: { ...customer, state: customer?.state || undefined },
          company: {
            ...company,
            logo_url: res?.logo_url || company?.logo_url,
            state: company?.state || undefined
          },
          warranty: {
            ...data?.warranty,
            items: data?.warranty?.items || defaultContractFormWarrantyItems
          },
          visible_blocks: visibleBlocks
        })
      }

      if (projectTypeDetails) {
        setProjectTypes(projectTypeDetails)
      }
    } finally {
      setFetchingInitialData(false)
    }
  }, [leadId, proposalId, getProposal, form])

  useEffect(() => {
    void handleInitialForm()
  }, [leadId, handleInitialForm])

  useEffect(() => {
    generateDocumentTitle("Add Contract")
  }, [])

  if (!leadId || !proposalId) {
    return <Empty description={"Please provide the lead ID or proposal ID."} />
  }

  if (fetchingInitialData) {
    return <Skeleton.Node active className={"size-full"} />
  }

  const handleCreateContract = async (data: Partial<ContractFormData>) => {
    const params = {
      ...data,
      proposal_id: proposalId,
      lead_id: leadId
    }

    setFormData(params)
    const html = await createContractPreview(params)?.unwrap()
    if (html) setPreviewHTML(html)
  }

  const handleGenerateContract = async () => {
    if (!formData) return

    const contractId = await createContract(formData)?.unwrap()
    if (contractId)
      generateContract({ data: formData, navigate, leadId, contractId })
  }

  const handleSendToApproval = async () => {
    if (!formData) return

    const contractId = await createContract(formData)?.unwrap()
    if (contractId)
      sendContractToApproval({ data: formData, navigate, leadId, contractId })
  }

  const handleSaveContract = () => {
    if (!formData) return
    saveContract({ data: formData, navigate })
  }

  return (
    <div className={"grid"}>
      {previewHTML && (
        <ContractAddPreviewHTML
          html={previewHTML}
          saving={saving}
          onClickGenerate={handleGenerateContract}
          onClickSaveDraft={handleSaveContract}
          onClickSendToApproval={handleSendToApproval}
          onClose={() => setPreviewHTML(null)}
        />
      )}
      <Flex
        vertical
        gap={"small"}
        className={"max-h-[calc(100vh-90px)] overflow-auto"}
      >
        <Flex
          align={"center"}
          justify={"space-between"}
          className={"sticky top-0 z-10 bg-background"}
        >
          <Typography.Title level={2}>Create New Contract</Typography.Title>
          <Link to={RoutesPaths.lead.view(leadId)}>
            <Button icon={<ArrowLeftOutlined />}>Back to Lead</Button>
          </Link>
        </Flex>
        <ContractForm
          innerForm={form}
          saving={saving}
          projectTypes={projectTypes}
          onSubmit={handleCreateContract}
          checkDirty={!previewHTML}
        />
      </Flex>
    </div>
  )
}

const mapStateToProps = (state: RootState) => ({
  saving: state.contracts.saveFetching
})

const mapDispatchToProps = {
  createContract,
  generateContract,
  saveContract,
  sendContractToApproval,
  createContractPreview: getContractPreview,
  getProposal: fetchProposal
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(ContractAddPage)
